
































































































































































































































import { Component, Vue, Prop, Emit, Inject } from 'vue-property-decorator';
import * as calc from '@/app/order-calculations-interface';
import OrderLineAllowanceCharge from './OrderLineAllowanceCharge.vue';
import Order from './Order.vue';
import ProductEdit from '@/components/Product/ProductEdit.vue';
import { DocumentShowType } from '../Shared/DocumentHeader.vue';
import { GQL_order_order, GQL_order_order_orderLines } from '@/queries/__generated__/order';
import { setProductToLine } from '@/app/Order';
import { WatchObjectAsJson } from '@/decorators/watch-object-as-json';
import Decimal from '@/utils/decimal';
import Bugsnag from '@bugsnag/js';
import { cloneDeep } from 'lodash';
import { ORDER_PRODUCTS_SEARCH } from './orderProductSearch';
import { GQL_orderProductsSearch_products } from '@/queries/__generated__/orderProductsSearch';
import { GQL_NorwegianVatSymbolEnum } from '@/queries/global';

export const isLineEmpty = (line: GQL_order_order_orderLines): boolean => {
  return !line.itemName && line.priceAmount.equals(0) && line.quantity.equals(1);
};

@Component({
  components: {
    OrderLineAllowanceCharge,
    ProductEdit,
  },
})
export default class OrderLine extends Vue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Prop(Object) public line!: any;
  @Prop(Boolean) public expanded!: boolean;
  @Prop(Object) public order!: GQL_order_order;

  @Inject() public documentComponent!: Order;
  @Inject() public documentShowType!: DocumentShowType;

  public inputsFocused = false;

  private created(): void {
    this.checkProductIntegrity();
  }

  get editable() {
    return this.documentComponent.reactiveProps.editable;
  }

  get isLastLine() {
    const { currentIndex, lastIndex } = { ...this.indexes };
    return currentIndex === lastIndex;
  }

  get nextLine() {
    const { currentIndex, lastIndex } = { ...this.indexes };
    const lines = this.documentComponent.$refs.orderLines as Vue[];
    return lines[currentIndex + 1] || lines[lastIndex];
  }

  private showProductPopup = false;
  private ORDER_PRODUCTS_SEARCH = ORDER_PRODUCTS_SEARCH

  public handleTab() {
    if (this.order.unit.unitSetting.unitInvoiceSetting.productGroups) return;

    if (this.isLastLine) this.addNewLine();
  }

  public async handleEnter(type: string) {
    if (!this.order.unit.unitSetting.unitInvoiceSetting.productGroups) {
      if (this.isLastLine) {
        // if we dont await this method, we can't go from page 1 to 2 with enter
        await this.addNewLine();
      }
    }
    this.jumpToSameFieldOnNextLine(type);
  }

  public async addNewLine() {
    // if we dont await this method, we can't go from page 1 to 2 with enter except on the product search field
    await this.documentComponent.addLine();
  }

  @Emit('update:expanded')
  public toggleExpand() {
    return !this.expanded;
  }

  @WatchObjectAsJson('line', { immediate: true })
  public calculate() {
    (this.line as calc.IOrderLine).lineExtensionAmount =
      calc.orderLineLineExtensionAmount(this.line as calc.IOrderLine);
    (this.line as calc.IOrderLine).taxAmount =
      calc.orderLineTaxAmount(this.line as calc.IOrderLine);
  }

  public handleFocus() {
    this.inputsFocused = true;
  }

  public handleBlur() {
    this.inputsFocused = false;
  }

  @Emit()
  public removeLine() {}

  public setProduct(product: GQL_orderProductsSearch_products) {
    if (!product.id) return;

    setProductToLine(
      this.line,
      product,
      this.order,
      this.documentComponent.activeNorwegianVatRates,
    );
    Bugsnag.leaveBreadcrumb('Product set', { line: cloneDeep(this.line), product });
    this.checkProductIntegrity();
  }

  public unsetProduct() {
    this.line.product = { id: null };
    this.line.itemName = null;
    this.line.productDescription = null;
    this.line.productDescriptionEnabled = false;
    this.line.priceAmount = new Decimal(0);
    this.line.sellersItemIdentificationId = null;
    this.line.unitOfMeasureCode = null;

    this.line.vatRateSymbol = '';
    this.line.taxPercent = new Decimal(0);
    this.line.taxExemptionReason = null;

    this.line.car = { id: null };
    this.line.project = { id: null };
    this.line.department = { id: null };

    Bugsnag.leaveBreadcrumb('Product unset', { line: cloneDeep(this.line) });
  }

  private checkProductIntegrity(): void {
    this.showProductPopup =
      this.checkTaxExemptionReason();
  }

  private checkTaxExemptionReason(): boolean {
    const vatSymbols = [ GQL_NorwegianVatSymbolEnum.EXEMPT, GQL_NorwegianVatSymbolEnum.ZERO ];
    return !!(
      this.line.product.id &&
      vatSymbols.includes(this.line.product.vatRateSymbol) &&
      !this.line.product.taxExemptionReason
    );
  }

  public setOrderedQuantity() {
    if (!this.documentComponent.displaySettings.invoiceLineOrderedQuantity) {
      this.line.orderedQuantity = this.line.quantity;
    }
  }

  public get indexes() {
    const lines = this.documentComponent.notDeletedLines;
    const currentIndex = lines.indexOf(this.line);
    const lastIndex = lines.length - 1;
    return { currentIndex, lastIndex };
  }

  public jumpToSameFieldOnNextLine(type: string) {
    // $refs is empty till nextTick, gives a TypeError without this, preventing us from focusing a field on a new page
    this.$nextTick(() => {
      if (!this.nextLine) return;
      const input = (this.nextLine.$refs[type] as Vue).$el.querySelector('input') as HTMLInputElement;
      input.focus();
      input.select();
    });
  }

  private ensureProductIsObject() {
    if (!this.line.product) this.line.product = { id: null };
  }

  private get isLineLastAndEmpty(): 'true' | 'false' {
    const lines = this.documentComponent.notDeletedLines;

    let thisLineEmpty: 'true' | 'false' = 'false';

    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i];

      if (isLineEmpty(line)) {
        if (line == this.line) {
          thisLineEmpty = 'true';
        }
      } else {
        return thisLineEmpty;
      }
    }

    return 'false';
  }
}
